import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import DefaultAvatar from '../components/common/DefaultAvatar';

type SettingsTab = 'profile' | 'notifications';

interface CostAlertThresholds {
  low_balance: number;
  daily_limit: number | null;
}

interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  created_at: string;
  preferences: {
    email_notifications: boolean;
    cost_alerts: boolean;
    security_alerts: boolean;
    newsletter: boolean;
    cost_alert_thresholds?: CostAlertThresholds;
  };
}

interface LoginSession {
  id: number;
  login_time: string | null;
  ip_address: string | null;
  user_agent: string | null;
}

const DEFAULT_PREFERENCES = {
  email_notifications: true,
  cost_alerts: true,
  security_alerts: true,
  newsletter: false,
};

function parseUserAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  // Extract browser
  let browser = 'Unknown browser';
  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  // Extract OS
  let os = 'Unknown OS';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  return `${browser} on ${os}`;
}

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Sessions
  const [showSessions, setShowSessions] = useState(false);
  const [sessions, setSessions] = useState<LoginSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState<number | null>(null);
  // Avatar
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${getApiUrl()}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setProfile({
            id: data.id,
            email: data.email,
            display_name: data.display_name || '',
            avatar_url: data.avatar_url,
            created_at: data.created_at || new Date().toISOString(),
            preferences: {
              ...DEFAULT_PREFERENCES,
              ...(data.preferences || {}),
            },
          });
        } else if (response.status === 401) {
          navigate('/login');
        } else {
          console.error('Failed to fetch profile:', response.status);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleSaveProfile = async () => {
    if (!profile) return;

    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          display_name: profile.display_name,
          preferences: profile.preferences,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Update local state with response
        setProfile({
          ...profile,
          display_name: data.display_name,
          preferences: { ...DEFAULT_PREFERENCES, ...data.preferences },
        });
        // Update token if new one provided
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const err = await response.json();
        setSaveError(err.detail || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/auth/delete-account`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        localStorage.removeItem('authToken');
        navigate('/login');
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to delete account');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `propalgos-data-export-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        alert('Failed to export data');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleViewSessions = async () => {
    if (showSessions) {
      setShowSessions(false);
      return;
    }
    setSessionsLoading(true);
    setShowSessions(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/auth/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        console.error('Failed to fetch sessions:', response.status);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId: number) => {
    setRevokingSessionId(sessionId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/auth/sessions/${sessionId}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setSessions(sessions.filter((s) => s.id !== sessionId));
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to revoke session');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Please select a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('File is too large. Maximum size is 2MB.');
      return;
    }

    setUploadingAvatar(true);
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${getApiUrl()}/auth/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (profile) {
          setProfile({ ...profile, avatar_url: data.avatar_url });
        }
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to upload avatar');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setUploadingAvatar(false);
      // Reset file input so the same file can be re-selected
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
              <p className="text-gray-400">
                Manage your account preferences and security settings
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-800 mb-8">
              {[
                { key: 'profile' as const, label: 'Profile' },
                { key: 'notifications' as const, label: 'Notifications' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === tab.key
                      ? 'text-white'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                  )}
                </button>
              ))}
            </div>

            {/* Profile Tab */}
            {activeTab === 'profile' && (
            <div className="space-y-8">
              {/* Profile Section */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <h2 className="text-xl font-bold text-white mb-6">Profile Information</h2>

                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <DefaultAvatar name={profile?.display_name} email={profile?.email} size="lg" />
                    )}
                    <div>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                      <button
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={uploadingAvatar}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploadingAvatar ? 'Uploading...' : 'Change Avatar'}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP. Max size 2MB.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={profile?.display_name || ''}
                        onChange={(e) => profile && setProfile({ ...profile, display_name: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter your display name"
                      />
                      <p className="text-xs text-gray-500 mt-1">This is how your name appears across the platform</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profile?.email || ''}
                        disabled
                        className="w-full bg-gray-700 border border-gray-600 text-gray-400 rounded-lg px-4 py-3 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed for security reasons</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Member Since
                    </label>
                    <p className="text-gray-400">
                      {profile && new Date(profile.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Security Section */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <h2 className="text-xl font-bold text-white mb-6">Security</h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h3 className="text-white font-medium">Two-Factor Authentication</h3>
                      <p className="text-sm text-gray-400">Add an extra layer of security to your account</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Coming soon</span>
                      <button
                        disabled
                        className="px-4 py-2 bg-green-600/50 text-white/50 rounded-lg text-sm font-medium cursor-not-allowed"
                      >
                        Enable 2FA
                      </button>
                    </div>
                  </div>

                  <div className="space-y-0">
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <h3 className="text-white font-medium">Active Sessions</h3>
                        <p className="text-sm text-gray-400">Manage devices that are logged into your account</p>
                      </div>
                      <button
                        onClick={handleViewSessions}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors"
                      >
                        {showSessions ? 'Hide Sessions' : 'View Sessions'}
                      </button>
                    </div>

                    {/* Sessions list (inline expandable) */}
                    {showSessions && (
                      <div className="pl-0 pb-3">
                        {sessionsLoading ? (
                          <div className="flex items-center gap-2 py-4 text-gray-400 text-sm">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                            Loading sessions...
                          </div>
                        ) : sessions.length === 0 ? (
                          <p className="text-sm text-gray-500 py-2">No active sessions found.</p>
                        ) : (
                          <div className="space-y-2 mt-2">
                            {sessions.map((session, index) => (
                              <div
                                key={session.id}
                                className="flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-3 border border-gray-700/50"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-white font-medium">
                                      {parseUserAgent(session.user_agent)}
                                    </span>
                                    {index === 0 && (
                                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                                        most recent
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                    {session.ip_address && <span>IP: {session.ip_address}</span>}
                                    {session.login_time && (
                                      <span>
                                        Logged in: {new Date(session.login_time).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                        })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRevokeSession(session.id)}
                                  disabled={revokingSessionId === session.id}
                                  className="ml-4 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {revokingSessionId === session.id ? 'Revoking...' : 'Revoke'}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h3 className="text-white font-medium">Download Data</h3>
                      <p className="text-sm text-gray-400">Export your account data and usage history</p>
                    </div>
                    <button
                      onClick={handleExportData}
                      disabled={exporting}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {exporting ? 'Exporting...' : 'Export Data'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Cost Alerts */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold text-white">Cost Alerts</h2>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={profile?.preferences.cost_alerts ?? true}
                      onChange={(e) => {
                        if (profile) {
                          setProfile({
                            ...profile,
                            preferences: { ...profile.preferences, cost_alerts: e.target.checked },
                          });
                        }
                      }}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${
                      profile?.preferences.cost_alerts ? 'bg-blue-600' : 'bg-gray-600'
                    }`}>
                      <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                        profile?.preferences.cost_alerts ? 'transform translate-x-5' : ''
                      }`}></div>
                    </div>
                  </label>
                </div>
                <p className="text-sm text-gray-400 mb-6">
                  Get notified when your balance is low or daily spending hits a limit
                </p>

                {profile?.preferences.cost_alerts && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Low Balance Threshold
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                        <input
                          type="number"
                          min={1}
                          step={0.5}
                          value={profile.preferences.cost_alert_thresholds?.low_balance ?? 5}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val)) {
                              const thresholds = profile.preferences.cost_alert_thresholds || { low_balance: 5, daily_limit: null };
                              setProfile({
                                ...profile,
                                preferences: {
                                  ...profile.preferences,
                                  cost_alert_thresholds: { ...thresholds, low_balance: val },
                                },
                              });
                            }
                          }}
                          className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-7 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">Alert when your balance drops below this amount</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Daily Spending Limit
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={profile.preferences.cost_alert_thresholds?.daily_limit ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const val = raw === '' ? null : parseFloat(raw);
                            const thresholds = profile.preferences.cost_alert_thresholds || { low_balance: 5, daily_limit: null };
                            setProfile({
                              ...profile,
                              preferences: {
                                ...profile.preferences,
                                cost_alert_thresholds: { ...thresholds, daily_limit: (val !== null && !isNaN(val)) ? val : null },
                              },
                            });
                          }}
                          placeholder="No limit"
                          className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-7 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-600"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">Alert when daily spending reaches this amount</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Danger Zone */}
              <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-6">
                <h2 className="text-xl font-bold text-red-400 mb-6">Danger Zone</h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h3 className="text-white font-medium">Delete Account</h3>
                      <p className="text-sm text-gray-400">Permanently delete your account and all associated data</p>
                    </div>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex items-center justify-end gap-4">
                {saveSuccess && (
                  <span className="text-green-400 text-sm">Changes saved successfully</span>
                )}
                {saveError && (
                  <span className="text-red-400 text-sm">{saveError}</span>
                )}
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="px-6 py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-8">
                <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                  <h2 className="text-xl font-bold text-white mb-6">Notification Preferences</h2>

                  <div className="space-y-4">
                    {[
                      { key: 'email_notifications', label: 'Email Notifications', description: 'Receive notifications about account activity' },
                      { key: 'security_alerts', label: 'Security Alerts', description: 'Important security-related notifications' },
                      { key: 'newsletter', label: 'Newsletter', description: 'Weekly updates and trading insights' }
                    ].map((setting) => (
                      <div key={setting.key} className="flex items-center justify-between py-3">
                        <div className="flex-1">
                          <h3 className="text-white font-medium">{setting.label}</h3>
                          <p className="text-sm text-gray-400">{setting.description}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={profile?.preferences[setting.key as keyof typeof profile.preferences] as boolean || false}
                            onChange={(e) => {
                              if (profile) {
                                setProfile({
                                  ...profile,
                                  preferences: { ...profile.preferences, [setting.key]: e.target.checked },
                                });
                              }
                            }}
                            className="sr-only"
                          />
                          <div className={`w-11 h-6 rounded-full transition-colors ${
                            profile?.preferences[setting.key as keyof typeof profile.preferences]
                              ? 'bg-blue-600'
                              : 'bg-gray-600'
                          }`}>
                            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                              profile?.preferences[setting.key as keyof typeof profile.preferences]
                                ? 'transform translate-x-5'
                                : ''
                            }`}></div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save Button for Notifications */}
                <div className="flex items-center justify-end gap-4">
                  {saveSuccess && (
                    <span className="text-green-400 text-sm">Changes saved successfully</span>
                  )}
                  {saveError && (
                    <span className="text-red-400 text-sm">{saveError}</span>
                  )}
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="px-6 py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Delete Account Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
                <h2 className="text-xl font-bold text-red-400 mb-4">Delete Account</h2>
                <p className="text-gray-300 mb-2">
                  Are you sure you want to delete your account? This will:
                </p>
                <ul className="list-disc list-inside text-gray-400 text-sm space-y-1 mb-4">
                  <li>Terminate all running instances</li>
                  <li>Cancel your Stripe subscription</li>
                  <li>Delete your data after 30 days</li>
                </ul>
                <p className="text-gray-500 text-xs mb-6">
                  This action cannot be undone. Your data will be permanently removed after a 30-day grace period.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
  );
}
